import { AppCard, AppPage } from '../../components/general';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { Fees } from './Fees';
import { MigrationReview } from './MigrationReview';
import { useFeeConfigurations, useHudLines, useFeeVersions } from '../../hooks/useFees';
import {
  FeeConfigurationViewModel,
  FeeHudLineViewModel,
  FeeRulesVersionViewModel,
  FeeMigrationDiff,
  FeeMigrationImportRequest,
} from '../../models/fees';
import { exportMigration, prepareImportMigration, importMigration } from '../../services/feeService';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { useSearchParams } from 'react-router-dom';

export const FeesContainer = () => {
  const { user, isLoading: authIsLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [migrationDiff, setMigrationDiff] = useState<FeeMigrationDiff | null>(null);
  const toastRef = useRef<Toast>(null);

  useEffect(() => {
    const versionIdParam = searchParams.get('versionId');
    if (versionIdParam) {
      const versionId = parseInt(versionIdParam, 10);
      if (!isNaN(versionId)) {
        setSelectedVersionId(versionId);
      }
    }
  }, [searchParams]);
  
  const { versionsQuery, publishVersion, deleteVersion } = useFeeVersions();
  const { feeConfigurationsQuery, deleteFeeConfiguration, importFeeConfigurations } = useFeeConfigurations();
  const { hudLinesQuery } = useHudLines(selectedVersionId);
  const { hudLinesQuery: draftHudLinesQuery } = useHudLines(null);

  const draftConfigCount = useMemo(() => {
    return (feeConfigurationsQuery.data ?? []).filter((c: FeeConfigurationViewModel) => !c.feeRulesVersionId).length;
  }, [feeConfigurationsQuery.data]);

  const draftHudLineCount = useMemo(() => {
    return (draftHudLinesQuery.data ?? []).filter((h: FeeHudLineViewModel) => !h.feeRulesVersionId).length;
  }, [draftHudLinesQuery.data]);

  const isLoading = authIsLoading || !user || feeConfigurationsQuery.isLoading || hudLinesQuery.isLoading || versionsQuery.isLoading;

  const handlePublish = async (description: string, publishDate?: string | null) => {
    try {
      const publishedVersion = await publishVersion({ description, publishDate });
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Version ${publishedVersion.versionNumber} published successfully`,
        life: 3000,
      });

      // Only switch to the new version for immediate publishes, not future-scheduled ones
      if (!publishDate) {
        setSelectedVersionId(publishedVersion.feeRulesVersionId);
      }
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to publish version',
        life: 5000,
      });
      throw error;
    }
  };

  const handleDeleteVersion = async (id: number) => {
    try {
      await deleteVersion(id);
      if (selectedVersionId === id) {
        setSelectedVersionId(null);
      }
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Scheduled publication deleted successfully',
        life: 3000,
      });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to delete publication',
        life: 5000,
      });
      throw error;
    }
  };

  const handleImport = async (file: File) => {
    try {
      const result = await importFeeConfigurations(file);
      
      if (result.successfulRows > 0) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Import Completed',
          detail: `Successfully imported ${result.successfulRows} of ${result.totalRows} rows`,
          life: 3000,
        });
      }
      
      return result;
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Import Failed',
        detail: error instanceof Error ? error.message : 'Failed to import fee configurations',
        life: 5000,
      });
      throw error;
    }
  };

  const handleMigrationExport = async () => {
    try {
      const blob = await exportMigration();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `migration-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Export Completed',
        detail: 'Migration file downloaded successfully',
        life: 3000,
      });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Export Failed',
        detail: error instanceof Error ? error.message : 'Failed to export migration',
        life: 5000,
      });
      throw error;
    }
  };

  const handleMigrationPrepareImport = async (file: File) => {
    const diff = await prepareImportMigration(file);
    setMigrationDiff(diff);
  };

  const handleMigrationImport = async (request: FeeMigrationImportRequest) => {
    const result = await importMigration(request);

    if (result.success) {
      feeConfigurationsQuery.refetch();
      hudLinesQuery.refetch();
    }

    return result;
  };

  const handleMigrationCancel = () => {
    setMigrationDiff(null);
  };

  if (isLoading) {
    return (
      <AppPage title="Fees" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Fees', to: '/products/fees' }, { label: 'Fee Configurations' }]}>
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading Fees...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (migrationDiff) {
    return (
      <>
        <Toast ref={toastRef} />
        <MigrationReview
          diff={migrationDiff}
          onImport={handleMigrationImport}
          onCancel={handleMigrationCancel}
        />
      </>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <Fees
        feeConfigurations={(feeConfigurationsQuery.data ?? []) as FeeConfigurationViewModel[]}
        hudLines={(hudLinesQuery.data ?? []) as FeeHudLineViewModel[]}
        versions={(versionsQuery.data ?? []) as FeeRulesVersionViewModel[]}
        selectedVersionId={selectedVersionId}
        onVersionChange={setSelectedVersionId}
        onPublish={handlePublish}
        onDeleteVersion={handleDeleteVersion}
        draftConfigCount={draftConfigCount}
        draftHudLineCount={draftHudLineCount}
        onImport={handleImport}
        onMigrationExport={handleMigrationExport}
        onMigrationPrepareImport={handleMigrationPrepareImport}
        onDelete={(id) => {
          if (id == null) return;
          deleteFeeConfiguration(id);
        }}
      />
    </>
  );
};

